import React, { useState, useEffect } from 'react';
import { Container, Grid, Button, Typography }from '@material-ui/core';
import { Card, CardContent, CardActions, TextField } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import SearchResults from '../components/Search/SearchResults';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useUserContext } from '../context/UserContext';
import { useCartContext } from '../context/CartContext';
import { cartActionTypes, cartItemActionTypes } from '../app/enums';
import { Alert } from '@material-ui/lab';
import { Carts, Searches } from '../api/api';
import { SearchType } from '../models/search.interface';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { isEmpty } from 'lodash';

const useStyles = makeStyles((theme: Theme) =>
  createStyles ({
    pageTitle: {
      color: theme.palette.primary.dark,
      fontWeight: 'bold',
    },
    gridSpacer: {
      minHeight: '12px',
    },
    container: {
      padding: 20,
    },
    card: {
      marginLeft: theme.spacing(1),
      width: '100%'
    },
    gridLine: {
      flexGrow: 1,
      margin: theme.spacing(1, 0, 1),
    },
    button: {
      margin: theme.spacing(0, 0, 0, 1),
    },
    alert: {
      width: '100%',
      margin: theme.spacing(1, 0, 1, 1),
    },
  }),
);

export const Search = () => {
  const classes = useStyles(); 
  const user = useUserContext();
  const cart = useCartContext();
  const self = user.state.self;
  const [ vin, setVin ] = useState('');
  const [ searchError, setSearchError] = useState('')
  const [ searchResult, setSearchResult ] = useState<SearchType>();
  const [ loading , setLoading ] = useState(false);
  const [ lotNumber, setLotNumber ] = useState<string>('')
  const [ seller, setSeller ] = useState<string>('') 
  const [ sellerError, setSellerError ] = useState<string>('');
  const [ lotNumberError, setLotNumberError ] = useState<string>('');
  const [ vinError, setVinError ] = useState<string>('');
  const isCopart = self.locations[0].company.name.toLowerCase() === 'copart' ? true : false;
  
  useEffect(() => {
    if(cart.state.cartId === '' && self.locations[0]){
      Carts.getSelfCarts(self.locations[0].id)
        .then((selfCartResponse) => {
          cart.dispatch({ type: cartActionTypes.LOAD_CART, cart: selfCartResponse[0] })

          if(cart.state.cartItems.length > 0){
            cart.state.cartItems.forEach((item) => {
              Searches.getSearch(item.searchId)
                .then((search) => {
                  cart.dispatch({ 
                    type: cartActionTypes.UPDATE_CART_ITEM, 
                    item: item, 
                    action: cartItemActionTypes.UPDATE_CART_ITEM_VEHICLE,
                    search: search 
                  })
                })
            })
          }
        })
        .catch((error) => {
          setSearchError(error.response.data.message)
        }) 
    }
  },[]);

  const handleChange = (event: any) => {
    setVinError('');
    setVin(event.currentTarget.value);
  };
   
  const handleButtonClick = () => {   
    callSearch();
  };

  const handleEnter = (event: React.KeyboardEvent) => {
    if(event.key === 'Enter'){
      callSearch();
    }
  }

  function callSearch(){
    setLoading(true)
    setSearchError('')
    
    if(isEmpty(vin)){
      setVinError('Required');
    }

    if(isEmpty(lotNumber) && isCopart) {
      setLotNumberError('Required');
    }

    if(isEmpty(seller) && isCopart) {
      setSellerError('Required');
    }

    if(isCopart && (isEmpty(seller) || isEmpty(lotNumber))){
      setLoading(false);
      return;
    }

    if(self.locations.length > 0 && !isEmpty(vin)){
      Searches.createSearch(vin,  self.locations[0].id, lotNumber, seller)
        .then((searchData) => {
          setSearchResult(searchData);
        }).catch((error) => {
          setSearchError(error.response.data.message);
          setSearchResult(undefined);
        }).finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }

  const clearSearch = () => {
    setSearchResult(undefined)
    setVin('');
    setVinError('');
    setSearchError('');
    setLotNumber('');
    setLotNumberError('');
    setSeller('');
    setSellerError('');
  }

  const handleLotChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setLotNumberError('');
    setLotNumber(event.currentTarget.value)
  };

  const handleSellerChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setSellerError('');
    setSeller(event.currentTarget.value)
  };

  return (
    <Container 
      aria-label="searchContainer"
      component="main" 
      maxWidth="sm" 
      className={classes.container}
    >
      <Grid container justifyContent="flex-start" alignItems="flex-start">
        <Typography 
          aria-label="searchPageTitle"
          className={classes.pageTitle}
          variant="h5"
        >
          Search
        </Typography>
      </Grid>
      <Grid container className={classes.gridSpacer} />
      <Grid container spacing={0}>
        <Card 
          aria-label="searchCard"
          className={classes.card}
        >
          <CardContent>
            <Typography className={classes.pageTitle}>
            Enter VIN for Quote:
          </Typography>
          <Grid container className={classes.gridLine}>
            <TextField
              id="vin"
              label="VIN"
              fullWidth
              variant="outlined"
              size="medium"
              required
              onChange={(e) => handleChange(e)}
              onPaste={(e) => handleChange(e)}
              onKeyPress={(e) => handleEnter(e)}
              value={vin}
              color="primary"
              helperText={vinError}
              error={!isEmpty(vinError)}
              inputProps={{ "data-testid": "vinInput" }}
            />
          </Grid>
          { self.locations.length > 0 && 
            <Grid container 
              spacing={1}
              justifyContent="flex-start"
            >
              <Grid container className={classes.gridSpacer} /> 
              <Grid item xs={6}>
                <TextField
                  id="lotNumber"
                  label="Lot Number"
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={lotNumber}
                  onChange={(e) => handleLotChange(e)}
                  required={isCopart}
                  helperText={lotNumberError}
                  error={!isEmpty(lotNumberError)}
                  inputProps={{ "data-testid": "lotNumberInput" }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  id="seller"
                  label="Seller"
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={seller}
                  onChange={(e) => handleSellerChange(e)}
                  required={isCopart}
                  helperText={sellerError}
                  error={!isEmpty(sellerError)}
                  inputProps={{ "data-testid": "sellerInput" }}
                />
              </Grid>
            </Grid>
          }
          </CardContent>
          <CardActions>
            <Grid container 
              justifyContent="flex-end"
            >
              <Button 
                aria-label="clearButton"
                onClick={clearSearch}
                color="primary"
                variant="outlined"
                className={classes.button}
              >
                Clear
              </Button>
              <Button 
                aria-label="searchButton"
                variant="contained"
                color="primary"
                size="medium"
                startIcon={<SearchIcon/>}
                onClick={handleButtonClick}
                className={classes.button}
              >
                Search
              </Button> 
            </Grid>
          </CardActions>
        </Card>
      </Grid>
      <Grid container className={classes.gridSpacer} /> 
      <Grid 
        container 
        spacing={0} 
        justifyContent="center"
        alignItems="center"
      > 
        { loading && <CircularProgress color="primary" /> }
        { searchError.length > 0 && 
          <div className={classes.alert}>
            <Alert severity="error">
              Unable to decode VIN
            </Alert>
          </div>
        }
        { searchResult &&
          <Card 
            aria-label="searchResultCard"
            className={classes.card}
          >
            <CardContent>
              <SearchResults searchResult={searchResult} vin={vin} clearSearch={clearSearch}/>
            </CardContent>
          </Card> 
        }
      </Grid>
    </Container>
  );
};

export default Search;
